import { Link, useLocation } from 'react-router-dom'
import { Button, SectionTitle } from '@azoya-spa/ui'
import '@azoya-spa/ui/styles.css'
import { useCatalog } from '../../hooks/useCatalog'
import { useSiteContent } from '../../hooks/useSiteContent'
import practicalInfoFallback from '../../api/fallback/practicalInfo.json'
import { frenchNumberToTelHref } from '../../utils/phone'
import { lienVersUnivers } from '../../utils/universDeSoins'
import { bookingCtaClicked, phoneClicked } from '../../analytics/events'
import './Error404.css'

// Deux arrivées, deux écrans. Celle qui tape une adresse de travers ne sait pas
// ce qu'elle cherchait ; celle qui clique « équipe » — une ancienne entrée de
// navigation, toujours routée ici — voulait savoir qui l'accueillera. Un seul
// écran obligerait à parler d'adresse erronée à quelqu'un qui a posé une
// question légitime.
const CAS = {
  generique: {
    titre: "Cette adresse ne correspond à aucune page du site.",
    texte:
      'Le lien a peut-être changé, ou la page a été retirée. Le reste du site est intact : les soins, les créneaux et les tarifs sont là où ils ont toujours été.',
    telephoneDabord: false,
  },
  team: {
    titre: 'Il n’y a pas de page « équipe » sur ce site.',
    texte:
      'Vous voulez savoir qui vous accueillera. C’est une question à laquelle le site ne répond pas encore, mais l’institut y répond en une minute au téléphone — et vous choisissez votre praticienne au moment de réserver.',
    telephoneDabord: true,
  },
}

// Le nombre d'univers appartient à l'administration, pas au code : la gérante
// en ajoute, en masque et en supprime. Écrit en dur, le titre se serait mis à
// mentir sur la page même dont le sujet est de ne pas égarer la visiteuse.
// Au-delà de la liste, le chiffre — approximatif vaut mieux que faux.
const NOMBRES_EN_LETTRES = [
  '', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six',
  'sept', 'huit', 'neuf', 'dix', 'onze', 'douze',
]

function titreDesUnivers(nombre) {
  if (nombre === 1) return 'Notre univers de soins'
  return `Nos ${NOMBRES_EN_LETTRES[nombre] ?? nombre} univers de soins`
}

function IconeTelephone({ couleur = '#fff', taille = 20 }) {
  return (
    <svg width={taille} height={taille} viewBox="0 0 24 24" fill="none" stroke={couleur} strokeWidth="1.9" aria-hidden="true">
      <path d="M4 4h4l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v4a1 1 0 0 1-1 1A16 16 0 0 1 3 5a1 1 0 0 1 1-1z" />
    </svg>
  )
}

export default function Error404() {
  const { pathname } = useLocation()
  const { catalog } = useCatalog()
  const info = useSiteContent('practical-info', practicalInfoFallback)


  const cas = pathname === '/team' ? CAS.team : CAS.generique
  const telHref = frenchNumberToTelHref(info.phone)
  const familles = catalog ?? []

  // Deux liens téléphone distincts sur cette page, et deux points d'accroche :
  // celui-ci n'est rendu que sur /team, celui du pied l'est sur toutes les 404.
  // N'en instrumenter qu'un raterait la moitié des appels partis d'ici.
  const appeler = (
    <a className="Error404-tel" href={telHref} onClick={() => phoneClicked()}>
      <IconeTelephone />
      Appeler le {info.phone}
    </a>
  )

  return (
    <div className="Error404">
      <div className="Error404-panneau">
        <div className="Error404-entete">
          <span className="Error404-pastille" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#e86f51" strokeWidth="1.7">
              <circle cx="12" cy="12" r="8" strokeDasharray="3 3.4" />
            </svg>
          </span>
          <span className="Error404-surtitre">Erreur 404 · adresse introuvable</span>
        </div>

        <h1>{cas.titre}</h1>
        <p className="Error404-texte">{cas.texte}</p>

        <div className="Error404-sorties">
          {cas.telephoneDabord && appeler}
          {/* Septième chemin vers /reserver-en-ligne, et le seul qui parte
              d'une visiteuse égarée : même raisonnement que pour le CTA du
              header, l'omettre fausserait la répartition entre emplacements —
              ce qui est précisément ce que la mesure cherche à établir. Le
              bouton voisin mène au catalogue et n'est pas un départ en
              réservation : il n'est pas instrumenté. */}
          <Button as={Link} to="/reserver-en-ligne" onClick={() => bookingCtaClicked('erreur_404')}>
            Réserver en ligne
          </Button>
          <Button variant="light" as={Link} to="/services">
            Voir tous nos soins
          </Button>
        </div>
      </div>

      {familles.length > 0 && (
        <div>
          <SectionTitle as="h2">{titreDesUnivers(familles.length)}</SectionTitle>
          <p className="Error404-familles-intro">
            Chacun ouvre sur la liste complète des prestations, avec les durées et les tarifs.
          </p>
          <div className="Error404-familles">
            {/* L'identifiant voyage dans l'adresse : sans lui, la page des
                prestations ne saurait pas laquelle des pastilles a été
                cliquée et rouvrirait la première — la visiteuse repartirait
                d'un clic précis vers un catalogue en vrac. */}
            {familles.map((famille) => (
              <Link className="Error404-famille" key={famille.id} to={lienVersUnivers(famille.id)}>
                <span className="Error404-famille-glyphe" aria-hidden="true">
                  {famille.icon}
                </span>
                <span className="Error404-famille-nom">{famille.name}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="Error404-pied">
        <span>Azoya Spa — {info.address}.</span>
        <a className="Error404-tel-discret" href={telHref} onClick={() => phoneClicked()}>
          {info.phone}
        </a>
      </div>
    </div>
  )
}
